// metaAdsSelection.js — pure derivations behind the Meta Ads settings modal's
// "Ad Accounts & Pages" tab. Same discipline as connectionSelection.js: plain functions over the
// JSON the endpoints already return, no React / DOM / fetch, so every row shape, every id used in
// a destructive request, and every word of user-facing copy is unit-testable under `node --test`.
//
// The page-agnostic selection algebra is IMPORTED from connectionSelection.js (25 passing tests)
// and re-exported here so the modal has one import site. Only the meta-specific pieces — the ad
// account row builder, single-select semantics, the removal payload, and the copy — live here.
//
// Vocabulary (matches the bulk-remove endpoint contract):
//   facebook_ad_account — an ad account the company can create campaigns in. `id` on the wire is
//                         the PROVIDER id (`ad_account_id`, the `act_…` string), matching how
//                         every other type in that endpoint is keyed. `pk` is the Rails id and is
//                         used only by PATCH /api/facebook/ad_accounts/:id/select.
//   facebook_page       — a Page, reused verbatim from the social tab via buildPageRows.
//
// Ad accounts are a RADIO group (exactly one `selected` drives Company#selected_ad_account), which
// is the opposite of Pages — several Pages can be active at once. Do not generalize across them.

import {
  FACEBOOK_PAGE,
  buildPageRows,
  clearRemovedKeys,
  connectionKey,
  sectionSelectAllLabel,
  sectionSelectionState,
  selectionLabel,
  toggleKey,
  toggleSection,
} from './connectionSelection.js';

// Re-exported unchanged so the modal imports selection behaviour from one place.
export {
  FACEBOOK_PAGE,
  buildPageRows,
  clearRemovedKeys,
  connectionKey,
  sectionSelectAllLabel,
  sectionSelectionState,
  selectionLabel,
  toggleKey,
  toggleSection,
};

export const AD_ACCOUNT = 'facebook_ad_account';

// ── Row building ────────────────────────────────────────────────────────────────────────────

// account_status is Meta's numeric-string enum; "1" is the only value that means the account can
// actually spend. Anything else is shown raw (it is more informative than a generic label), and a
// missing value falls back to "Inactive". Mapping preserved from the pre-rework markup.
const statusLabel = (accountStatus) => (
  accountStatus === '1' ? 'Active' : (accountStatus || 'Inactive')
);

// Fixture = SocialConnectionPayloads#ad_account_payload:
// { id, ad_account_id, name, account_status, currency, selected }
export const buildAdAccountRows = (adAccounts) => {
  const accounts = Array.isArray(adAccounts) ? adAccounts : [];

  return accounts.map((a) => ({
    key: connectionKey(AD_ACCOUNT, a.ad_account_id),
    type: AD_ACCOUNT,
    // The select endpoint looks accounts up by primary key; the bulk-remove endpoint looks them up
    // by provider id. Both are carried so neither caller has to guess.
    pk: a.id,
    adAccountId: a.ad_account_id,
    name: a.name || a.ad_account_id,
    meta: `${a.currency || 'USD'} · ${statusLabel(a.account_status)}`,
    selected: Boolean(a.selected),
  }));
};

// ── Single-select ───────────────────────────────────────────────────────────────────────────

// Optimistic mirror of PATCH /api/facebook/ad_accounts/:id/select, whose server side is a
// clear-then-set inside one transaction. Exactly one row ends up selected; re-selecting the
// already-selected row is a no-op, matching the endpoint's idempotency guarantee. An unknown key
// changes nothing rather than leaving the list with zero selected.
export const applySelection = (rows, key) => {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.some((r) => r.key === key)) return list;
  return list.map((r) => ({ ...r, selected: r.key === key }));
};

// ── Request payload ─────────────────────────────────────────────────────────────────────────

// The request body for DELETE /api/facebook/connections. Ad accounts are keyed by their provider
// id (NOT the Rails pk); page rows already carry the Meta page_id as `id`.
export const toMetaRemovalPayload = (items) => (items || []).map((row) => (
  row.type === AD_ACCOUNT
    ? { type: AD_ACCOUNT, id: row.adAccountId }
    : { type: row.type, id: row.id }
));

// ── Copy ────────────────────────────────────────────────────────────────────────────────────
// Every claim below is literally true of the code as it stands. The load-bearing one: removal is
// Vesta-local bookkeeping — there is no Graph API call in any teardown path — so campaigns already
// running on Meta keep running and keep spending. Nothing here may imply Vesta pauses them.

const oxford = (names) => {
  const list = (names || []).filter(Boolean);
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
};

export const metaItemTypeLabel = (row) => (row && row.type === AD_ACCOUNT ? 'Ad account' : 'Page');

export const metaConfirmTitle = (n) => (
  n === 1 ? 'Remove this connection?' : `Remove ${n} connections?`
);

export const metaConfirmBody = (items) => {
  const list = items || [];
  const adAccounts = list.filter((r) => r.type === AD_ACCOUNT).length;

  if (adAccounts > 0) {
    const subject = adAccounts === 1 ? 'the ad account' : 'these ad accounts';
    const object = adAccounts === 1 ? 'it' : 'them';
    return `Vesta will disconnect ${subject} and stop creating and syncing campaigns for ${object}. `
      + 'Campaigns already running on Meta are not paused — they keep running and spending. '
      + 'Manage them in Meta Ads Manager. '
      + 'Accounts with active campaigns in Vesta keep their campaign history but are hidden from this list.';
  }

  const pages = list.length === 1 ? 'this Page' : `these ${list.length} Pages`;
  return `Vesta will disconnect ${pages}. Ads and posts already published stay live on Facebook.`;
};

// Amber note slot. Shown only when the currently selected ad account is among the items — removing
// it silently retargets every future campaign, so the successor is named out loud.
export const selectedAccountNote = (removedName, successorName) => (
  successorName
    ? `⚠ ${removedName} is your selected ad account. After removal, new campaigns will use ${successorName}.`
    : `⚠ ${removedName} is your selected ad account. After removal no ad account will be connected, `
      + 'and campaign creation will pause until you connect one.'
);

export const metaKeepLabel = (n) => (n === 1 ? 'Keep it' : 'Keep them');

export const metaRemoveCtaLabel = (n) => (n === 1 ? 'Remove' : `Remove all ${n}`);

// result: { removed, removedAdAccounts: [{ ad_account_id, name, outcome }], promotedName }
// `removed_ad_accounts` (the raw response key) is accepted too so the caller can hand the payload
// straight through. `promotedName` is derived by the caller from the response's ad_accounts list.
export const removalSuccessToast = (result) => {
  const r = result || {};
  const n = r.removed || 0;
  const removedAdAccounts = r.removedAdAccounts || r.removed_ad_accounts || [];
  const archived = removedAdAccounts
    .filter((a) => a && a.outcome === 'archived')
    .map((a) => a.name || a.ad_account_id);

  let message = `Removed ${n} connection${n === 1 ? '' : 's'}.`;
  if (archived.length > 0) message += ` Campaign history kept for ${oxford(archived)}.`;
  if (r.promotedName) message += ` ${r.promotedName} is now your selected ad account.`;
  return message;
};

export const selectSuccessToast = (name) => `${name} is now your selected ad account.`;

// This endpoint never talks to Meta, so the failure is local — say so rather than blaming Meta.
export const REMOVAL_ERROR_META = "Nothing was removed — the change didn't save. Try again.";

export const SELECT_ERROR = "Couldn't change the selected account — nothing changed. Try again.";

export const PAGE_TOGGLE_ERROR = "Couldn't update the Page — try again.";
